import React, { useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import axiosConfig from 'src/Resources/axiosConfig';

interface DocumentImageProps {
  docId?: number;
  src?: string;
  alt?: string;
  maxHeight?: number;
  // Compact mode for table rows: constrains the width too and shrinks the loading
  // placeholder so a row does not jump height while the blob is fetched.
  maxWidth?: number;
  placeholderHeight?: number;
}

function DocumentImage({
  docId,
  src,
  alt = '',
  maxHeight = 480,
  maxWidth,
  placeholderHeight = 80
}: DocumentImageProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (src) {
      setBlobUrl(src);
      return;
    }
    if (!docId) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    axiosConfig
      .get('/DownloadDocument', { params: { id: docId }, responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const url = window.URL.createObjectURL(new Blob([res.data]));
        createdUrl = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [docId, src]);

  if (failed) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color: 'text.disabled',
          fontSize: '0.85rem',
          my: 0.5
        }}
      >
        <BrokenImageIcon fontSize="small" />
        {alt || 'Image unavailable'}
      </Box>
    );
  }

  if (!blobUrl) {
    return (
      <Box
        sx={{
          display: 'inline-block',
          width: maxWidth ?? 120,
          height: placeholderHeight,
          bgcolor: 'action.hover',
          borderRadius: 1,
          my: 0.5
        }}
      />
    );
  }

  return (
    <>
      <Box
        component="img"
        src={blobUrl}
        alt={alt}
        onClick={() => setOpen(true)}
        sx={{
          display: 'block',
          maxWidth: maxWidth ?? '100%',
          maxHeight,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'zoom-in',
          my: 0.5
        }}
      />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'common.black' }}>
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'common.white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={blobUrl}
            alt={alt}
            sx={{
              display: 'block',
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain'
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default DocumentImage;
